import { useState, useMemo } from 'react'

import {
	EuiBasicTable,
	EuiButton,
	EuiFlexGroup,
	EuiFlexItem,
	EuiPage,
	EuiPageBody,
	EuiPageSection,
	EuiPanel,
	EuiProgress,
} from '@equipmentshare/ds2'

import PageTitle from 'views/components/PageTitle/PageTitle'
import StyledHeader from 'views/components/StyledHeader'
import SearchBar from 'views/components/SearchBar'
import RefreshButton from 'views/components/RefreshButton'
import CursorPagination from 'views/components/CursorPagination'

import { useProductClassesQuery, ProductClassesQuery, Permission } from '../../../graphql'

import { TABLE_HEIGHT } from 'views/helpers/utils'
import { getAddProductClassModal, getEditProductClassModal } from './components/modals'
import { useDispatchSetSelectedModal } from 'contexts/modal-context/modal-context'
import { checkPermissions } from 'components/PermissionsCheck/helpers'
import usePermissions from 'hooks/usePermissions'
import getColumns from './columns'
import PermissionsCheck from 'components/PermissionsCheck'
import { heapEvent } from '@equipmentshare/heaper'

// Extract the actual ProductClass type returned by the query
type ProductClassNode = ProductClassesQuery['productClasses']['edges'][number]['node']

export default function ProductClasses() {
	const [ search, setSearch ] = useState( '' )
	const [ rowSize, setRowSize ] = useState( 20 )

	const { data, loading, refetch } = useProductClassesQuery( {
		variables: {
			search,
			paging: {
				first: rowSize,
			}
		}
	} )
	const setSelectedModal = useDispatchSetSelectedModal()
	const { userPermissions } = usePermissions( [ Permission.CanUpdateProductClass ] )

	const canEdit = checkPermissions( userPermissions, [ Permission.CanUpdateProductClass ] )

	const getNextPage = ( cursor: string ) => () => {
		refetch( { paging: { first: rowSize, after: cursor } } )
	}

	const getPreviousPage = ( cursor: string ) => () => {
		refetch( { paging: { last: rowSize, before: cursor } } )
	}

	const onSearch = ( s: string ) => {
		heapEvent( 'Inventory - Product Classes - Change - Search Input', { search_term: s } )
		setSearch( s )
	}

	const productClasses = useMemo( () => {
		if ( data?.productClasses?.edges ) {
			return data.productClasses.edges.map( ( edge ) => edge.node )
		}
		return []
	}, [ data ] )

	const getNoItemsMessage = () => {
		if ( loading ) {
			return 'Loading data'
		}

		return 'No records found'
	}

	const onAddClick = () => {
		heapEvent( 'Inventory - Product Classes - Click - Add Product Class', {} )
		setSelectedModal( getAddProductClassModal( async() => await refetch() ) )
	}

	const onEdit = ( productClass: ProductClassNode ) => {
		heapEvent( 'Inventory - Product Classes - Click - Edit', {} )
		setSelectedModal( getEditProductClassModal( productClass ) )
	}

	const columns = getColumns<ProductClassNode>( canEdit, onEdit )

	const fullHeight = 'eui-fullHeight'
	return (
		<EuiPage paddingSize="l">
			<EuiPageBody className={fullHeight}>
				<PageTitle title="Product Classes" />
				<StyledHeader>
					<EuiFlexGroup wrap>
						<EuiFlexItem style={{ minWidth: 677 }}>
							<div className="content searchFilter">
								<SearchBar search={search} setSearch={onSearch} />
								<RefreshButton refetchFunction={refetch} />
							</div>
						</EuiFlexItem>
						<EuiFlexItem grow={false}>
							<div className="content actions">
								<PermissionsCheck permissionsToCheck={[ Permission.CanCreateProductClass ]}>
									<EuiButton
										fill
										onClick={onAddClick}
									>
										Add class
									</EuiButton>
								</PermissionsCheck>
							</div>
						</EuiFlexItem>
					</EuiFlexGroup>
				</StyledHeader>

				<EuiPageSection className={fullHeight} paddingSize="none">
					<EuiPanel
						paddingSize="none"
						className="eui-yScroll"
						hasShadow={false}
						style={{ height: TABLE_HEIGHT }}
					>
						{ loading && <EuiProgress /> }
						<EuiBasicTable
							loading={loading}
							noItemsMessage={getNoItemsMessage()}
							columns={columns}
							items={productClasses}
						/>
					</EuiPanel>
					{ !!data?.productClasses.pageInfo && (
						<CursorPagination
							pageInfo={data.productClasses.pageInfo}
							pageForward={getNextPage}
							pageBackward={getPreviousPage}
							rowSize={rowSize}
							setRowSize={setRowSize}
						/>
					) }
				</EuiPageSection>
			</EuiPageBody>
		</EuiPage>
	)
}
